import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { Subject, takeUntil } from 'rxjs';
import { FirQbuSourcingDeleteRecordService } from './fir-qbu-sourcing-delete-record.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  firstGridRowObjectFromPayload,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-fir-qbu-sourcing-delete-record',
    templateUrl: './fir-qbu-sourcing-delete-record.component.html',
    styleUrls: ['./fir-qbu-sourcing-delete-record.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class FirQbuSourcingDeleteRecordComponent implements OnInit, OnDestroy {

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private destroy$ = new Subject<void>();

  filterParams: any = {};
  globalParams: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};
  rowData: any = {};

  modalTitle = 'Delete QBU Sourcing Record';
  userSettings: any;
  logged_in_user: string = '';
  loading = false;

  deleteParams: any[] = [];
  checkLockedObj: any[] = [];

  constructor(
    private firQbuSourcingDeleteRecordService: FirQbuSourcingDeleteRecordService,
    private gtwSnackbarService: GtwSnackbarService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.init();
    this.gtwWorkspace.setTabActive.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    });
    this.gtwGlobal.globalParamsChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    });
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const activeData: any = activeScreen?.data || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    this.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    this.filterParams = normalizeFilterParams(activeScreen) || {};

    const raw =
      activeData?.selected?.rowData ??
      activeData?.rowData ??
      rowPayloadFromActiveScreen(activeScreen);
    this.rowData = firstGridRowObjectFromPayload(raw);
  }

  init() {
    try {
      if (this.GENERAL_CONFIG && this.USER_SETTINGS) {
        this.userSettings = this.USER_SETTINGS;
        this.logged_in_user = this.userSettings?.user?.sso_id || '';
      }

      if (this.rowData && Object.keys(this.rowData).length > 0 && this.filterParams) {
        this.setupDeleteParams();
      }
    } catch (e) {
      console.error('Error in FirQbuSourcingDeleteRecord init:', e);
    }
  }

  private setupDeleteParams() {
    if (!this.rowData || !Object.keys(this.rowData).length) {
      return;
    }

    this.checkLockedObj = [{
      me_code: this.rowData.ME_CODE,
      consol_group_key: this.filterParams?.filing_key || this.globalParams?.filing_key,
      scenario_key: this.filterParams?.scenario || this.globalParams?.scenario,
      combination_key: this.rowData.COMBINATION_KEY,
      tax_year: this.filterParams?.tax_year || this.globalParams?.tax_year,
      scenarioCode: this.globalParams?.scenarioCode || '',
      is_issue_key_enabled: 'N'
    }];

    this.deleteParams = [{
      ho_leid: this.rowData.HO_LEID,
      ho_cdr_no: this.rowData.HO_CDR_NO,
      leid: this.rowData.LEID,
      cdr_no: this.rowData.CDR_NO,
      me_code: this.rowData.ME_CODE,
      acct: this.rowData.ACCT,
      source_type: this.rowData.SOURCE_TYPE,
      source_id: this.rowData.SOURCE_ID,
      qbu_amt_ind: this.rowData.QBU_AMT_IND,
      rec_type: this.rowData.REC_TYPE,
      tp_key: this.rowData.TP_KEY,
      reporting_period: this.rowData.REPORTING_PERIOD,
      acct_key: this.rowData.ACCT_KEY,
      chart_key: this.rowData.CHART_KEY,
      issue_key: this.rowData.ISSUE_KEY,
      ho_combination_key: this.rowData.HO_COMBINATION_KEY
    }];
  }

  deleteData() {
    if (!this.deleteParams.length || !this.checkLockedObj.length) {
      this.gtwSnackbarService.openSnackBar('Missing required data for deletion', 'gtw-snackbar--red');
      return;
    }

    this.loading = true;

    const tempData = {
      TAX_YEAR: this.filterParams.tax_year,
      SCENARIO: this.filterParams.scenario,
      JCD_KEY: this.filterParams?.jcd_key || this.globalParams?.jcd_key,
      SSO_ID: this.logged_in_user,
      DEL_PARAMS: JSON.stringify(this.deleteParams)
    };

    this.firQbuSourcingDeleteRecordService.deleteQbuSourcingRecord(
      this.GENERAL_CONFIG.base_url,
      tempData,
      this.globalParams || {},
      this.checkLockedObj,
      this.filterParams,
      this.rowData
    ).pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      (data: any) => {
        this.loading = false;

        if (data.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar('QBU Sourcing record deleted successfully', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          let errorMessage = 'Error deleting QBU Sourcing record';
          if (data.errorMessage && data.errorMessage !== 'null') {
            errorMessage += '. ' + data.errorMessage;
          }
          this.gtwSnackbarService.openSnackBar(errorMessage, 'gtw-snackbar--red');
          this.requestGridUpdate.emit('cancel');
        }
      },
      (error: any) => {
        this.loading = false;
        let errorMessage = 'Error Deleting Data';
        if (error?.error?.errorMessage) {
          errorMessage += '. ' + error.error.errorMessage;
        }
        this.gtwSnackbarService.openSnackBar(errorMessage, 'gtw-snackbar--red');
        this.requestGridUpdate.emit('cancel');
      }
    );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
